// 产品选型向导主页面 — 4 步流程（含内嵌九宫格结果）
import { useState, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Sparkles, Loader2 } from 'lucide-react';
import { RegionStep, type RegionId } from './wizard/RegionStep';
import { ScenarioStep, type ScenarioId, SCENARIO_DEFAULTS } from './wizard/ScenarioStep';
import { ParameterStep, type WizardParams } from './wizard/ParameterStep';
import { RecommendationResult } from './wizard/RecommendationResult';
import { scoreProducts, type ScoredProduct } from '../utils/recommendation';

type WizardStep = 0 | 1 | 2 | 3;

const STEP_LABELS = ['选择区域', '选择场景', '输入参数', '推荐结果'];

const DEFAULT_PARAMS: WizardParams = {
  viewingDistance: 8,
  lighting: 'indoor',
  budget: 'standard',
};

const REGION_LABELS: Record<string, string> = {
  china: '中国区', north_america: '北美区', northwest_europe: '西北欧区',
  southeast_europe: '东南欧区', mea: '中东非区', east_asia: '东亚区',
  south_asia: '南亚区', south_america: '南美区', russia_cis: '俄语区',
  non_certified: '非认证地区',
};

const SCENARIO_LABELS: Record<string, string> = {
  command_center: '指挥中心', commercial: '商业显示', conference: '会议/教育',
  outdoor: '户外广告', sports: '体育赛事', rental: '租赁舞台',
  creative: '创意显示', cinema: '影院/XR',
};

export function ProductSelectionWizard() {
  const [step, setStep] = useState<WizardStep>(0);
  const [selectedRegion, setSelectedRegion] = useState<RegionId | null>(null);
  const [selectedScenario, setSelectedScenario] = useState<ScenarioId | null>(null);
  const [params, setParams] = useState<WizardParams>(DEFAULT_PARAMS);
  const [results, setResults] = useState<ScoredProduct[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const canProceed = step === 0 ? !!selectedRegion : step === 1 ? !!selectedScenario : true;

  const handleNext = useCallback(() => {
    if (step < 2 && canProceed) setStep((step + 1) as WizardStep);
  }, [step, canProceed]);

  const handleBack = useCallback(() => {
    if (step > 0) setStep((step - 1) as WizardStep);
  }, [step]);

  /** 生成推荐 → 显示结果 */
  const handleGenerate = useCallback(async () => {
    if (!selectedRegion || !selectedScenario) return;
    setIsGenerating(true);

    // 用 setTimeout 让 UI 先渲染 loading 状态
    await new Promise(r => setTimeout(r, 300));

    try {
      const scored = scoreProducts(selectedRegion, selectedScenario, params);
      setResults(scored);
      setStep(3);
    } catch (error) {
      console.error('生成推荐失败:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [selectedRegion, selectedScenario, params]);

  /** 重新选择 → 回到 Step 0 */
  const handleRegenerate = useCallback(() => {
    setStep(0);
    setResults([]);
  }, []);

  return (
    <div
      className="min-h-screen bg-[#f5f5f7]"
      style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', sans-serif" }}
    >
      {/* 顶部进度条 */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200/60 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-center gap-0">
            {STEP_LABELS.map((label, i) => (
              <div key={i} className="flex items-center">
                {i > 0 && (
                  <div className={`w-12 h-px mx-2 ${i <= step ? 'bg-[#0071e3]' : 'bg-gray-200'}`} />
                )}
                <button
                  onClick={() => { if (i < step) setStep(i as WizardStep); }}
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-[13px] font-medium transition-all duration-300 ${
                    i === step
                      ? 'bg-[#0071e3] text-white shadow-sm'
                      : i < step
                      ? 'bg-[#0071e3]/10 text-[#0071e3] cursor-pointer hover:bg-[#0071e3]/15'
                      : 'bg-gray-100 text-[#86868b]'
                  }`}
                  disabled={i > step}
                >
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                    i === step ? 'bg-white/25 text-white' :
                    i < step ? 'bg-[#0071e3] text-white' :
                    'bg-gray-200 text-gray-400'
                  }`}>
                    {i < step ? '✓' : i + 1}
                  </span>
                  <span className="hidden sm:inline">{label}</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 内容区 */}
      <div className="max-w-5xl mx-auto px-6 py-8 pb-28">
        {step === 0 && (
          <RegionStep
            selectedRegion={selectedRegion}
            onSelect={(r) => setSelectedRegion(r)}
          />
        )}
        {step === 1 && selectedRegion && (
          <ScenarioStep
            selectedRegion={selectedRegion}
            selectedScenario={selectedScenario}
            onSelect={(s) => {
              setSelectedScenario(s);
              // 场景联动默认参数
              const defaults = SCENARIO_DEFAULTS[s];
              if (defaults) {
                setParams(prev => ({
                  ...prev,
                  viewingDistance: defaults.viewingDistance,
                  lighting: defaults.lighting as WizardParams['lighting'],
                }));
              }
            }}
          />
        )}
        {step === 2 && selectedRegion && selectedScenario && (
          <ParameterStep
            selectedRegion={selectedRegion}
            selectedScenario={selectedScenario}
            params={params}
            onParamsChange={setParams}
          />
        )}
        {step === 3 && selectedRegion && selectedScenario && (
          <RecommendationResult
            results={results}
            regionLabel={REGION_LABELS[selectedRegion] || selectedRegion}
            scenarioLabel={SCENARIO_LABELS[selectedScenario] || selectedScenario}
            viewingDistance={params.viewingDistance}
            lighting={params.lighting}
            onRegenerate={handleRegenerate}
          />
        )}
      </div>

      {/* 生成中遮罩 */}
      {isGenerating && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-[#0071e3] animate-spin mx-auto mb-4" />
            <p className="text-[17px] font-medium text-[#1d1d1f]">正在生成推荐方案…</p>
            <p className="text-[13px] text-[#86868b] mt-1">智能匹配最优产品组合</p>
          </div>
        </div>
      )}

      {/* 底部操作栏 — 结果页不显示 */}
      {step < 3 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-t border-gray-200/60 z-10">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
            {step > 0 ? (
              <button
                onClick={handleBack}
                className="flex items-center gap-1.5 text-[14px] text-[#0071e3] hover:text-[#0077ED] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                上一步
              </button>
            ) : (
              <div />
            )}

            {step < 2 ? (
              <button
                onClick={handleNext}
                disabled={!canProceed}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-[14px] font-medium transition-all duration-300 ${
                  canProceed
                    ? 'bg-[#0071e3] text-white hover:bg-[#0077ED] shadow-sm'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                下一步
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#0071e3] text-white text-[14px] font-medium
                  hover:bg-[#0077ED] transition-all duration-300 shadow-sm disabled:opacity-50"
              >
                <Sparkles className="w-4 h-4" />
                生成推荐
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductSelectionWizard;
